import { createSlice } from "@reduxjs/toolkit";
import { rgsPlay } from "./spinSlice";
import { initializeGame, refreshGameData } from "./gameSettingsSlice";
import { FEATURES_CONFIG } from "../../featuresConfig";
import StorageManager from "../../utils/StorageManager";

/**
 * Free Rounds Slice
 * Manages casino-granted free rounds (promotional spins)
 *
 * State fields:
 * - status: "active" | "played" | null
 * - betLevel: Index of bet in bets array
 * - betValue: Fixed bet amount for free rounds (in subunits)
 * - performed: Number of free rounds completed so far
 * - count: Total number of free rounds granted
 * - totalWin: Cumulative win across all free rounds (from balance.free_rounds)
 * - completionPending: Flag to indicate completion flow should start
 * - freeRoundsEnded: Flag set when completion popup opens (UI should hide)
 * - reloadPending: Flag for reload popup timing (show after game renders)
 */

const initialState = {
  status: null,
  betLevel: null,
  betValue: null,
  performed: 0,
  count: 0,
  totalWin: 0,
  completionPending: false,
  freeRoundsEnded: false,
  reloadPending: false,
  isFreeRoundsCompletionReload: false, // Flag: reloading after completion (status = "played")
  balanceAlreadyUpdated: false, // Flag: balance already updated (H&W outro or API)
  shouldDeductNextSpin: false, // Flag: force bet deduction on next spin after warning closes
};

const freeRoundsSlice = createSlice({
  name: "freeRounds",
  initialState,
  reducers: {
    /**
     * Set free rounds data from API response
     * @param {object} action.payload - Free rounds data from init/spin API
     */
    setFreeRounds(state, action) {
      const { status, bet_level, bet_value, performed, count, totalWin } =
        action.payload;

      state.status = status;
      state.betLevel = bet_level;
      state.betValue = bet_value;
      state.performed = performed;
      state.count = count;
      state.totalWin = totalWin !== undefined ? totalWin : 0;
      state.freeRoundsEnded = false; // Reset when new free rounds start
    },

    /**
     * Increment the performed count after each free round spin
     */
    incrementPerformed(state) {
      state.performed += 1;
    },

    /**
     * Mark free rounds as completed (status = "played")
     */
    completeFreeRounds(state) {
      state.status = "played";
    },

    /**
     * Clear free rounds state (return to initial state)
     * Now fully resets including freeRoundsEnded flag
     */
    clearFreeRounds(state) {
      Object.assign(state, initialState);
    },

    setCompletionPending(state) {
      state.completionPending = true;
    },

    clearCompletionPending(state) {
      state.completionPending = false;
    },

    setFreeRoundsEnded(state) {
      state.freeRoundsEnded = true;
    },

    clearFreeRoundsEnded(state) {
      state.freeRoundsEnded = false;
    },

    /**
     * Set reload pending flag
     * Used during game reload to defer popup until game is rendered
     */
    setReloadPending(state) {
      state.reloadPending = true;
    },

    /**
     * Clear reload pending flag
     */
    clearReloadPending(state) {
      state.reloadPending = false;
    },

    /**
     * Clear completion reload flag
     * Called when user closes completion popups
     */
    clearFreeRoundsCompletionReload(state) {
      state.isFreeRoundsCompletionReload = false;
    },

    /**
     * Set balance already updated flag
     * Called when balance is updated from H&W outro or refreshGameData
     */
    setBalanceAlreadyUpdated(state, action) {
      state.balanceAlreadyUpdated = action.payload;
    },

    /**
     * Set flag to force bet deduction on next spin
     * Called when free rounds warning popup (206) closes
     */
    setShouldDeductNextSpin(state, action) {
      state.shouldDeductNextSpin = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rgsPlay.pending, (state, action) => {
        // Skip all free rounds logic if feature is disabled
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) {
          return;
        }

        // Clear free rounds state and storage when next spin starts after completion
        // If status is "played", the next spin is a base game spin (not free rounds)
        if (state.status === "played") {
          StorageManager.clearFreeRound();
          // Reset to initial state BUT keep the shouldDeductNextSpin flag
          const shouldDeduct = state.shouldDeductNextSpin;
          const newState = { ...initialState, shouldDeductNextSpin: shouldDeduct };
          return newState;
        }

        // NOTE: performed count is updated from API response in rgsPlay.fulfilled
        // No manual increment needed here
        
        if (state.status === null && state.freeRoundsEnded) {
          // Reset the ended flag when returning to regular gameplay
          state.freeRoundsEnded = false;
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        // Clear the shouldDeductNextSpin flag after bet deduction happens
        if (state.shouldDeductNextSpin) {
          state.shouldDeductNextSpin = false;
        }
        
        // Skip all free rounds logic if feature is disabled
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) {
          return;
        }

        const { featureState, freeRounds, freeRoundsTotalWin } = action.payload;

        // Use universal featureState if available, otherwise fall back to legacy freeRounds
        if (featureState && featureState.type === "freeRounds") {
          if (featureState.phase === "active") {
            // CRITICAL: If we're currently completing a free rounds session (status === "played" or completionPending),
            // do NOT activate the new free rounds yet. They should wait for the user to dismiss popups and spin.
            // The new free rounds will be activated when the user spins (status will be "played" -> cleared in pending)
            if (state.status === "played" || state.completionPending) {
              // Don't update state - let completion flow finish first
              return;
            }
            
            state.status = "active";
            state.betLevel = featureState.data.betLevel;
            state.betValue = featureState.data.betValue;
            state.performed = featureState.data.performed;
            state.count = featureState.data.count;
            state.totalWin = featureState.data.totalWin || 0;
          } else if (featureState.phase === "completion") {
            state.status = "played";
            state.performed = featureState.data.performed;
            state.count = featureState.data.count;
            state.totalWin = featureState.data.totalWin || 0;
          }

          // Don't set completionPending here - let FreeRoundsCompletionHandler
          // detect the status change and set it after animations complete

          // RGS now handles saving completion data to localStorage
          // No need to save here anymore
        } else if (freeRounds) {
          // Legacy fallback: Use old freeRounds object if featureState not available
          state.status = freeRounds.status;
          state.betLevel = freeRounds.bet_level;
          state.betValue = freeRounds.bet_value;
          state.performed = freeRounds.performed;
          state.count = freeRounds.count;
        } else {
          // CRITICAL: No free rounds data in response means we're back to base game
          // Ensure state is fully cleared (status should already be null from pending)
          if (state.status !== null) {
            state.status = null;
            state.betLevel = null;
            state.betValue = null;
            state.performed = 0;
            state.count = 0;
            state.totalWin = 0;
          }
        }

        if (freeRoundsTotalWin !== undefined && freeRoundsTotalWin !== null) {
          state.totalWin = freeRoundsTotalWin;
        }
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        // Skip all free rounds logic if feature is disabled
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) {
          return;
        }

        const { result } = action.payload;
        const { featureState, reloadState, freeRoundsData } = result;

        // Handle completion reload from RGS
        if (reloadState?.needsReload && reloadState.type === "freeRounds" && reloadState.phase === "completion") {
          // Set completion reload flag
          state.isFreeRoundsCompletionReload = true;

          // Restore free rounds state from reloadState
          state.status = "played";
          state.totalWin = reloadState.data.totalWin;
          state.count = reloadState.data.count;
          state.performed = reloadState.data.performed;

          // Set flags to trigger completion flow
          state.completionPending = true;
          state.freeRoundsEnded = false;
          state.reloadPending = true;
        }
        // Handle H&W completion reload when H&W was triggered during Free Rounds
        else if (reloadState?.needsReload && reloadState.type === "holdAndWin" && reloadState.phase === "completion" && freeRoundsData) {
          // Set completion reload flag - balance already includes Free Rounds + H&W wins
          state.isFreeRoundsCompletionReload = true;

          // Restore free rounds state from freeRoundsData
          // CRITICAL: Check if ALL free rounds are actually completed (performed === count)
          const allRoundsPlayed = freeRoundsData.performed === freeRoundsData.count;
          
          // If all rounds played, status is "played" and trigger completion flow
          // If more rounds left, status stays "active" and just show "rounds left" popup
          state.status = allRoundsPlayed ? "played" : "active";
          state.totalWin = freeRoundsData.totalWin;
          state.count = freeRoundsData.count;
          state.performed = freeRoundsData.performed;

          // CRITICAL: Only trigger completion flow if ALL rounds are played
          // Otherwise just show "rounds left" popup via reloadPending
          state.completionPending = allRoundsPlayed;
          state.freeRoundsEnded = false;
          state.reloadPending = true;
        }
        // Handle active free rounds from RGS (primary feature)
        else if (featureState?.type === "freeRounds" && featureState.phase === "active") {
          // CRITICAL: If we're currently completing a free rounds session, do NOT activate new ones yet
          // They should wait for user to dismiss popups and spin
          if (state.status === "played" || state.completionPending) {
            // Don't update state - let completion flow finish first
            return;
          }
          
          state.status = "active";
          state.count = featureState.data.count;
          state.performed = featureState.data.performed;
          state.betValue = featureState.data.betValue;
          state.betLevel = featureState.data.betLevel;
          state.totalWin = featureState.data.totalWin || 0;
          state.reloadPending = true;
        }
        // NEW: Handle free rounds when Hold & Win is on top (secondary feature scenario)
        // Free Rounds is the base feature, Hold & Win triggered during Free Rounds
        // ALWAYS set as "active" - even on last round (12/12) until user closes outro
        else if (freeRoundsData?.status === "active") {
          state.status = "active";
          state.count = freeRoundsData.count;
          state.performed = freeRoundsData.performed;
          state.betValue = freeRoundsData.betValue;
          state.betLevel = freeRoundsData.betLevel;
          state.totalWin = freeRoundsData.totalWin || 0;
          state.reloadPending = true;
        }
      })
      // Handle refreshGameData - for error 205 free rounds flow and H&W completion during Free Rounds
      .addCase(refreshGameData.fulfilled, (state, action) => {
        // Skip if feature disabled
        if (!FEATURES_CONFIG.FREE_ROUNDS.ENABLED) {
          return;
        }

        const { result } = action.payload;
        const { featureState, freeRoundsData } = result;

        // Handle active free rounds (error 205 flow)
        // Only activate if transitioning from null/played to active (prevent infinite loop)
        if (
          featureState?.type === "freeRounds" && 
          featureState.phase === "active" &&
          state.status !== "active" &&
          state.status !== "played" &&
          !state.completionPending
        ) {

          state.status = "active";
          state.count = featureState.data.count;
          state.performed = featureState.data.performed;
          state.betValue = featureState.data.betValue;
          state.betLevel = featureState.data.betLevel;
          state.totalWin = 0;
          state.completionPending = false;
          state.freeRoundsEnded = false;
          state.isFreeRoundsCompletionReload = false;
          state.reloadPending = true;
        }
        // Handle Free Rounds data from localStorage (H&W completion during Free Rounds)
        // CRITICAL: Don't clear Free Rounds just because API doesn't send them
        // API only provides valid data for intro/active, not completion
        else if (freeRoundsData?.status === "active") {
          // Check if this is the last round (completion scenario)
          const isLastRound = freeRoundsData.performed === freeRoundsData.count;
          
          // If current status is 'played', keep it as 'played' (don't revert to 'active')
          // This prevents FreeRoundsCounter from reappearing after completion
          if (state.status !== 'played') {
            state.status = isLastRound ? "played" : "active";
          }
          
          state.count = freeRoundsData.count;
          state.performed = freeRoundsData.performed;
          state.betValue = freeRoundsData.betValue;
          state.betLevel = freeRoundsData.betLevel;
          // CRITICAL: Don't overwrite totalWin if current state has a higher value
          // This happens when H&W completes and updates totalWin, then refreshGameData is called
          // We should keep the higher value (which includes H&W wins)
          const currentTotalWin = state.totalWin || 0;
          const storageTotalWin = freeRoundsData.totalWin || 0;
          state.totalWin = Math.max(currentTotalWin, storageTotalWin);
        }
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

/**
 * Check if free rounds are currently active
 */
export const selectFreeRoundsActive = (state) =>
  state.freeRounds.status === "active";

/**
 * Get total count of free rounds granted
 */
export const selectFreeRoundsCount = (state) => state.freeRounds.count;

/**
 * Get number of free rounds performed so far
 */
export const selectFreeRoundsPerformed = (state) => state.freeRounds.performed;

/**
 * Get total win accumulated across all free rounds
 */
export const selectFreeRoundsTotalWin = (state) => state.freeRounds.totalWin;

/**
 * Get the fixed bet value for free rounds
 */
export const selectFreeRoundsBet = (state) => state.freeRounds.betValue;

/**
 * Get the free rounds status
 */
export const selectFreeRoundsStatus = (state) => state.freeRounds.status;

export const selectFreeRoundsCompletionPending = (state) =>
  state.freeRounds.completionPending;

/**
 * Check if free rounds have ended (completion popup opened)
 */
export const selectFreeRoundsEnded = (state) =>
  state.freeRounds.freeRoundsEnded;

/**
 * Check if reload popup is pending (waiting for game to render)
 */
export const selectFreeRoundsReloadPending = (state) =>
  state.freeRounds.reloadPending;

/**
 * Check if current session is a free rounds completion reload
 */
export const selectIsFreeRoundsCompletionReload = (state) =>
  state.freeRounds.isFreeRoundsCompletionReload;

export const selectBalanceAlreadyUpdated = (state) =>
  state.freeRounds.balanceAlreadyUpdated;

export const selectShouldDeductNextSpin = (state) =>
  state.freeRounds.shouldDeductNextSpin;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  setFreeRounds,
  incrementPerformed,
  completeFreeRounds,
  clearFreeRounds,
  setCompletionPending,
  clearCompletionPending,
  setFreeRoundsEnded,
  clearFreeRoundsEnded,
  setReloadPending,
  clearReloadPending,
  clearFreeRoundsCompletionReload,
  setBalanceAlreadyUpdated,
  setShouldDeductNextSpin,
} = freeRoundsSlice.actions;

// Export the reducer to integrate into the Redux store
export default freeRoundsSlice.reducer;
